import { Component, forwardRef, Input, EventEmitter, Output, OnInit } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BehaviorSubject, concat, from, Observable, Subject } from "rxjs";
import { RecordService } from "../../../../../services/record.service";
import {
  debounceTime,
  distinctUntilChanged,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { isNil, isString, toString } from 'lodash-es';
import {
  data_get,
  observe,
  OptionalValue,
  when,
  transform,
} from '../../../../utils/common';
import {
  FieldmagicRelateOption,
  RelateSearchService,
} from '../../../../../services/relate-search.service';

export { FieldmagicRelateOption } from '../../../../../services/relate-search.service';

@Component({
  selector: 'fieldmagic-relate-input',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RelateInputComponent),
      multi: true,
    }
  ],
  template: `
    <div
      *ngIf="! standalone"
      class="form-group"
    >
      <fieldmagic-text
        *ngIf="bDisplayLabel"
        purpose="input"
        [content]="label"
        [withRequiredMarker]="withRequiredMarker"
      >
      </fieldmagic-text>
      <ng-container *ngTemplateOutlet="input"></ng-container>
    </div>
    <ng-container
      *ngIf="standalone"
      [ngTemplateOutlet]="input">
    </ng-container>

    <ng-template #input>
      <ng-select
        class="fieldmagic-relate"
        [items]="options$ | async"
        [typeahead]="typeahead$"
        [loading]="loading$ | async"
        [clearable]="clearable"
        [(ngModel)]="selected"
        (ngModelChange)="onModelChange($event)"
        bindLabel="text"
        (focus)="onTouched()"
        appendTo="body"
        placeholder="{{ placeholder | translate }}"
        [class.fieldmagic-input-has-error]="(errors | filled) || (errorneous$ | async)"
        [multiple]="multiple"
        [disabled]="disabled$ | async"
        [bindValue]="bindValue"
        (open)="onOpen()"
      >
        <ng-template ng-option-tmp let-item="item" class="value-text">
          {{ item.text | translate }}
        </ng-template>

        <ng-template ng-label-tmp let-item="item" let-clear="clear">
          <span class="ng-value-label">{{ item.text | translate }}</span>
          <span
            *ngIf="multiple"
            class="ng-value-icon right selected-close"
            (click)="clear(item)"
          >
            x
          </span>
        </ng-template>
      </ng-select>
      <fieldmagic-input-errors
        *ngIf="errors | filled"
        [errors]="errors"
      ></fieldmagic-input-errors>
    </ng-template>

  `,
  styles: [
    `.fieldmagic-relate ::ng-deep .ng-select-container {
      height: 40px !important;
      font-size: 12px;
    }`,
    `.fieldmagic-relate ::ng-deep .ng-placeholder {
      top: inherit !important;
    }`,
  ]
})
export class RelateInputComponent implements ControlValueAccessor, OnInit {
  @Input() label: string;

  @Input('filter') filter: FieldmagicRelateInputFilter;

  @Input('placeholder') placeholder: string;

  @Input() moduleName: string;

  @Input() bDisplayLabel: boolean = true;

  @Input('disabled')
  set disabled(value: boolean) {
    this.disabled$.next(value);
  }

  @Input() withServerAdditionalFilter: boolean = true;

  @Input() standalone: boolean = false;

  @Input() withRequiredMarker: boolean = false;

  @Input() errors: string[] | string = [];

  @Input() clearable: boolean = true;

  @Input() multiple: boolean = false;

  @Input() bindValue?: string;

  @Input()
  set errorneous(value: boolean) {
    this.errorneous$.next(value);
  }

  /**
   * Emits whenever the value is changed.
   *
   * @type {EventEmitter<Select>}
   */
  @Output('change') $onChange = new EventEmitter<FieldmagicRelateInputChangedEvent>();

  typeahead$: Subject<string> = new Subject<string>();
  options$: Observable<FieldmagicRelateOption[]> = from([]);

  onTouched: () => void = () => {};
  onChange: FieldmagicRelateInputOnChangedHandler = (_) => {};

  selected: OptionalValue<FieldmagicRelateInputValue> | OptionalValue<FieldmagicRelateInputValue[]>;

  readonly loading$ = new BehaviorSubject<boolean>(false);
  readonly disabled$ = new BehaviorSubject<boolean>(false);
  readonly errorneous$ = new BehaviorSubject<boolean>(false);

  /**
   * @param {RecordService} records
   */
  constructor(
    protected records: RecordService,
    private readonly _relates: RelateSearchService,
  ) {}

  ngOnInit(): void {
    this.options$ = concat(... [
      this.options$,
      this.typeahead$.pipe(
        (isString(this.selected)) ? startWith('') : tap(),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((term: string) => observe({
          before: () => this.loading$.next(true),
          after: () => this.loading$.next(false),
          observable: () => this._relates.search(this.moduleName, {
            term: term,
            with_additional_filter: this.withServerAdditionalFilter,
            filter: this.filter,
            record_id: transform(this.selected, {
              transformer: (selected) => when<string>(isString(selected), {
                then: () => toString(selected),
                else: () => data_get(selected, 'id'),
              })
            }),
          }),
        })),
      ),
    ]);
  }

  writeValue = (selected?: FieldmagicRelateInputValue) => this.selected = selected;

  registerOnTouched = (fn: () => void) => this.onTouched = fn;
  registerOnChange = (fn: FieldmagicRelateInputOnChangedHandler) => this.onChange = (selected) => {
    if (! isNil(fn)) {
      fn(selected);
    }

    this.$onChange.emit(selected);
  };

  onOpen(): void {
    this.typeahead$.next('');
  }

  onModelChange(value: FieldmagicRelateInputValue): void {
    this.writeValue(value);
    this.onChange(value);
    this.$onChange.emit(value);
  }

  setDisabledState = (disabled: boolean) => this.disabled$.next(disabled);
}

export type FieldmagicRelateInputChangedEvent<T = any> = FieldmagicRelateOption<T>;
export type FieldmagicRelateInputFilter = {[key: string]: any};
export type FieldmagicRelateInputValue<T = Record<string, any>> = T extends Record<string, any>
  ? FieldmagicRelateOption<T>
  : string;

// @deprcated: FieldmagicRelateOption
export type RelateOption<T = any> = FieldmagicRelateOption<T>;

// @deprcated: FieldmagicRelateInputFilter
export type Filter = FieldmagicRelateInputFilter;

export type FieldmagicRelateInputOnChangedHandler<T = Record<string, any>> = (
  value?:
    | OptionalValue<FieldmagicRelateInputValue<T>>
    | OptionalValue<FieldmagicRelateInputValue<T>[]>,
) => void;
